// Spec ref: docs/superpowers/specs/2026-05-21-backend-extension-ui-wireup.md #3
// Profilo — wired real component (was MockupPage prior to PR #5).
// Sections: Avatar · Anagrafica · Specializzazioni · Preferenze ·
// Notifiche · Sicurezza · Storico attività.
import { useState } from 'react'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { hasTokens } from '../lib/auth'
import { canTriggerScrape } from '../lib/permissions'
import { useLogout, useMeExtended } from '../hooks/useAuth'
import { ChangePasswordModal } from '../components/ChangePasswordModal'
import { ProfileAvatar } from '../components/profilo/ProfileAvatar'
import { ProfileAnagrafica } from '../components/profilo/ProfileAnagrafica'
import { ProfileSpecializzazioni } from '../components/profilo/ProfileSpecializzazioni'
import { ProfilePreferenze } from '../components/profilo/ProfilePreferenze'
import { ProfileNotifiche } from '../components/profilo/ProfileNotifiche'
import { ProfileSicurezza } from '../components/profilo/ProfileSicurezza'
import { ProfileGoogleCalendar } from '../components/profilo/ProfileGoogleCalendar'
import { ProfileCalendarResync } from '../components/profilo/ProfileCalendarResync'
import { ActivityTimeline } from '../components/ActivityTimeline'
import { CardSkeleton } from '../components/Skeleton'

type ProfileSearch = {
  // Ritorno dal flusso OAuth Google (backend: /calendar/oauth/callback).
  // `error` + `reason`: l'utente ha annullato / Google ha rifiutato.
  calendar?: 'connected' | 'disconnected' | 'error'
  reason?: 'denied' | 'failed'
}

export const Route = createFileRoute('/profile')({
  beforeLoad: () => {
    if (!hasTokens()) {
      throw redirect({ to: '/login' })
    }
  },
  validateSearch: (search: Record<string, unknown>): ProfileSearch => {
    const calendar =
      search.calendar === 'connected' ||
      search.calendar === 'disconnected' ||
      search.calendar === 'error'
        ? search.calendar
        : undefined
    return {
      calendar,
      reason:
        calendar === 'error' &&
        (search.reason === 'denied' || search.reason === 'failed')
          ? search.reason
          : undefined,
    }
  },
  component: ProfilePage,
})

function ProfilePage() {
  const { data: me, isLoading, isError } = useMeExtended()
  const navigate = useNavigate()
  const search = Route.useSearch()
  const logout = useLogout()
  const [pwOpen, setPwOpen] = useState(false)

  function onLogout() {
    logout()
    void navigate({ to: '/login' })
  }

  return (
    <>
      {me && (
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--accent-border)',
            borderRadius: 'var(--r-md)',
            padding: 'var(--sp-2) var(--sp-3)',
            margin: '0 0 var(--sp-3) 0',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-3)',
            flexWrap: 'wrap',
            fontSize: 'var(--fs-12)',
          }}
        >
          <span style={{ fontWeight: 'var(--fw-semibold)' }}>
            {me.surname ? `${me.name} ${me.surname}` : me.name}
          </span>
          <span className="muted">
            {me.email} ·{' '}
            <span className="role-pill">
              {(me as { title?: string | null }).title || me.role}
            </span>
            {me.secondary_roles && me.secondary_roles.length > 0 && (
              <> · {me.secondary_roles.join(', ')}</>
            )}
          </span>
          <span style={{ flex: 1 }} />
          <button type="button" className="btn sm" onClick={onLogout}>
            Logout
          </button>
        </div>
      )}

      {isLoading && <CardSkeleton />}
      {isError && (
        <div
          className="muted"
          style={{ padding: 'var(--sp-4)', fontStyle: 'italic' }}
        >
          Errore caricamento profilo.
        </div>
      )}

      {me && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-3)',
          }}
        >
          <ProfileAvatar me={me} />
          <ProfileAnagrafica me={me} />
          <ProfileSpecializzazioni me={me} />
          <ProfilePreferenze me={me} />
          <ProfileNotifiche me={me} />
          <ProfileGoogleCalendar
            returned={search.calendar}
            reason={search.reason}
            onDismissReturned={() =>
              void navigate({ to: '/profile', search: {}, replace: true })
            }
          />
          {canTriggerScrape(me) && <ProfileCalendarResync />}
          <ProfileSicurezza
            me={me}
            onChangePassword={() => setPwOpen(true)}
          />
          <ActivityTimeline
            params={{ user_id: me.id, page_size: 50 }}
            title="Storico attività"
            showFilters
          />
        </div>
      )}

      {pwOpen && (
        <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
      )}
    </>
  )
}
